/**
 * The kitchen display keeps itself up to date.
 *
 * The screen in the kitchen is a touch panel on the wall: it has no keyboard,
 * nobody closes it, and it can stay on the same page for days. Every release of
 * this app therefore used to need someone to remember to press "Aggiorna" —
 * and until they did, the wall screen quietly ran last week's code. That is
 * exactly how a venue with a switch turned on can see nothing change.
 * `main.tsx` has carried a hand-bumped version stamp for this reason, with a
 * comment about kiosk tablets serving stale cache after a build.
 *
 * So the page checks for itself. Vite writes the bundle name with a content
 * hash into `index.html`, so the name IS the version: fetch the page, read the
 * name, compare it with the one actually running. No file to generate, no step
 * to remember at deploy time, and it works with the hand copy the kitchen
 * display is deployed by.
 *
 * Two rules keep it from being a nuisance. It never reloads under the cook's
 * finger — it waits for a quiet moment, unless the new version has been waiting
 * long enough that the wait itself is the problem. And it reloads at most once
 * per version: if the browser hands back the old page anyway, the screen is left
 * alone instead of reloading in a loop forever.
 */

/** Where the running page says which bundle it loaded. */
const BUNDLE_PATTERN = /assets\/index-[A-Za-z0-9_-]+\.js/;

/** How long the screen must be untouched before an update may interrupt it. */
export const DEFAULT_QUIET_MS = 30_000;

/** After this long, a pending update goes in even on a busy screen. */
export const DEFAULT_FORCE_AFTER_MS = 30 * 60_000;

/** How often to look. The page is half a kilobyte; this costs nothing. */
export const DEFAULT_CHECK_MS = 5 * 60_000;

/**
 * The bundle name inside an `index.html`, or null when there is none to find —
 * a captive-portal page, an error page, or the dev server, which serves modules
 * instead of a built bundle.
 */
export function extractBundleName(html: string): string | null {
  const match = BUNDLE_PATTERN.exec(html ?? "");
  return match ? match[0] : null;
}

export interface ReloadInput {
  /** The bundle this page is running. Null when it cannot be told (dev). */
  current: string | null;
  /** The bundle the server is serving now. Null when the check failed. */
  latest: string | null;
  /** The bundle this page has already reloaded for, once. */
  reloadedFor: string | null;
  /** Since the last touch on the screen. */
  msSinceInteraction: number;
  /** Since this page first saw `latest` on the server. */
  msSinceFirstSeen: number;
  quietMs?: number;
  forceAfterMs?: number;
}

export interface ReloadDecision {
  reload: boolean;
  /** Why — logged, so a screen that is not updating can be asked. */
  reason:
    | "check-failed"
    | "unknown-current"
    | "up-to-date"
    | "already-reloaded"
    | "busy"
    | "new-version"
    | "new-version-overdue";
}

/**
 * Whether to reload now.
 *
 * Every "no" is a considered one: a failed check tells us nothing, a page that
 * cannot name its own bundle has nothing to compare, and a version we already
 * reloaded for and are still not running means the reload did not take — so
 * reloading again would only repeat, forever, on a screen nobody is watching.
 */
export function decideReload(input: ReloadInput): ReloadDecision {
  const quietMs = input.quietMs ?? DEFAULT_QUIET_MS;
  const forceAfterMs = input.forceAfterMs ?? DEFAULT_FORCE_AFTER_MS;

  if (!input.latest) return { reload: false, reason: "check-failed" };
  if (!input.current) return { reload: false, reason: "unknown-current" };
  if (input.current === input.latest) return { reload: false, reason: "up-to-date" };
  if (input.reloadedFor === input.latest) {
    return { reload: false, reason: "already-reloaded" };
  }
  if (input.msSinceFirstSeen >= forceAfterMs) {
    return { reload: true, reason: "new-version-overdue" };
  }
  if (input.msSinceInteraction < quietMs) return { reload: false, reason: "busy" };
  return { reload: true, reason: "new-version" };
}

/** The bundle the running page loaded, read off its own script tag. */
export function runningBundleName(doc: Document = document): string | null {
  const scripts = Array.from(doc.querySelectorAll("script[src]")) as HTMLScriptElement[];
  for (const script of scripts) {
    const name = extractBundleName(script.getAttribute("src") ?? "");
    if (name) return name;
  }
  return null;
}

const RELOADED_FOR_KEY = "kds.reloadedFor";

function readReloadedFor(): string | null {
  try {
    return window.localStorage.getItem(RELOADED_FOR_KEY);
  } catch {
    // A kiosk with storage disabled loses only the loop guard's memory; the
    // check still works, it just cannot remember having tried.
    return null;
  }
}

function rememberReloadedFor(bundle: string): void {
  try {
    window.localStorage.setItem(RELOADED_FOR_KEY, bundle);
  } catch {
    /* see above */
  }
}

export interface AutoUpdateOptions {
  checkMs?: number;
  quietMs?: number;
  forceAfterMs?: number;
  /** Overridden in tests. */
  now?: () => number;
  reload?: () => void;
  fetchPage?: () => Promise<string>;
}

/**
 * Starts watching for a new version. Returns the function that stops it.
 *
 * Does nothing outside a browser, and nothing when the page cannot name its own
 * bundle — which is the dev server, where the page reloads by itself anyway.
 */
export function startAutoUpdate(options: AutoUpdateOptions = {}): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => {};
  }

  const now = options.now ?? (() => Date.now());
  const reload = options.reload ?? (() => window.location.reload());
  const fetchPage =
    options.fetchPage ??
    (async () => {
      // `no-store`, or the check would read the very cache it exists to defeat.
      const res = await fetch("/", { cache: "no-store" });
      return res.text();
    });

  const current = runningBundleName();
  if (!current) return () => {};

  let lastInteraction = now();
  let firstSeen: { bundle: string; at: number } | null = null;

  const touch = () => {
    lastInteraction = now();
  };
  window.addEventListener("pointerdown", touch, { passive: true });
  window.addEventListener("keydown", touch, { passive: true });

  const check = async () => {
    let latest: string | null = null;
    try {
      latest = extractBundleName(await fetchPage());
    } catch {
      latest = null; // offline, or nginx restarting: nothing to conclude
    }

    if (latest && (!firstSeen || firstSeen.bundle !== latest)) {
      firstSeen = { bundle: latest, at: now() };
    }

    const decision = decideReload({
      current,
      latest,
      reloadedFor: readReloadedFor(),
      msSinceInteraction: now() - lastInteraction,
      msSinceFirstSeen: firstSeen ? now() - firstSeen.at : 0,
      quietMs: options.quietMs,
      forceAfterMs: options.forceAfterMs,
    });

    if (decision.reload && latest) {
      rememberReloadedFor(latest);
      // eslint-disable-next-line no-console
      console.log(`[KDS] nuova versione (${latest}), ricarico — ${decision.reason}`);
      reload();
    }
  };

  const timer = setInterval(check, options.checkMs ?? DEFAULT_CHECK_MS);
  void check();

  return () => {
    clearInterval(timer);
    window.removeEventListener("pointerdown", touch);
    window.removeEventListener("keydown", touch);
  };
}
